import React, { FC, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Controller, ControllerRenderProps } from 'react-hook-form';
import { useUncontrolledInput } from 'modules/core/hooks';
import { IFormField } from 'modules/core/types/form';
import { useTranslation } from 'react-i18next';
import { getTiers } from 'store/selectors/lenders.selector';
import { IOption } from 'modules/core/types';
import { ILenderTierTerm } from 'modules/core/types/lender';

import { TermInputGroupUI, TermDropdownContainer, TermDropdownToggle, TermDropdownMenu, TermMenuItem } from './styles';
import { SHOP_BY_CALLBACK_INITIAL_VALUES } from '../Lender.const';

export interface ITermDropdownProps extends IFormField {
  isInvalid?: boolean;
}

export const TermDropdown: FC<ITermDropdownProps> = ({
  name,
  validate,
  rules,
  changeCallback,
  disabled,
  isInvalid,
  onToggle,
}) => {
  const { t } = useTranslation();
  const { controlField, errorMessage, hasError, fieldValue } = useUncontrolledInput(name);
  const tiers = useSelector(getTiers);
  const selectedTerm = fieldValue as IOption | undefined;
  const isSelectedDefault = selectedTerm === undefined || selectedTerm === null;
  const placeholder = t('components.lender.shopByCallback.term.placeholder');
  const toggleTitle = isSelectedDefault ? placeholder : selectedTerm.title;

  const termOptions = useMemo(() => {
    if (tiers.length === 0) return [];

    const firstTier = tiers[0];
    const tierTerms: ILenderTierTerm[] = (firstTier as any).tier_terms || [];

    const uniqueTerms = Array.from(new Set(tierTerms.map((term) => term.term_in_months))).sort((a, b) => b - a);

    return uniqueTerms.map((term) => ({
      title: String(term),
      value: term,
    }));
  }, [tiers]);

  const renderOptions = useCallback(
    ({ onChange, onBlur }: ControllerRenderProps) =>
      termOptions.map((option) => (
        <TermMenuItem
          onClick={(e: any) => {
            onChange(option);

            if (selectedTerm?.value !== option.value && changeCallback) {
              changeCallback(e);
            }
          }}
          onBlur={onBlur}
          key={option.value}
        >
          {option.title}
        </TermMenuItem>
      )),
    [termOptions, selectedTerm, changeCallback],
  );

  const isInvalidState = Boolean(errorMessage) || hasError || isInvalid;
  const isDisabled = disabled || termOptions.length === 0;
  const showGreenBorder = isSelectedDefault && !isInvalidState && !isDisabled;

  const renderDropdown = useCallback(
    (renderProps: any) => (
      <TermInputGroupUI
        $isPlaceholder={isSelectedDefault}
        $isInvalid={isInvalidState}
        $showGreenBorder={showGreenBorder}
      >
        <TermDropdownContainer
          disabled={isDisabled}
          id={name}
          onToggle={(isOpen: boolean) => {
            onToggle && onToggle(isOpen);
          }}
        >
          <TermDropdownToggle disabled={isDisabled} title={toggleTitle}>
            {toggleTitle}
          </TermDropdownToggle>
          <TermDropdownMenu>{renderOptions(renderProps)}</TermDropdownMenu>
        </TermDropdownContainer>
      </TermInputGroupUI>
    ),
    [isSelectedDefault, isInvalidState, showGreenBorder, isDisabled, name, toggleTitle, renderOptions, onToggle],
  );

  return (
    <Controller
      name={name}
      render={renderDropdown}
      control={controlField}
      defaultValue={SHOP_BY_CALLBACK_INITIAL_VALUES.sbcTerm}
      rules={{
        ...rules,
        validate,
      }}
    />
  );
};
